// custom-accordion.js
// Robust, accessible accordion with better browser support
// - Adds ARIA attributes and keyboard support
// - Handles dynamic content (images) via ResizeObserver/MutationObserver fallbacks
// - Graceful fallback if custom elements are unsupported

(function () {
    // If Custom Elements aren't supported, just expose content and bail.
    if (!('customElements' in window)) {
      document.addEventListener('DOMContentLoaded', () => {
        document.querySelectorAll('custom-accordion .accordion-content').forEach((el) => {
          el.style.maxHeight = 'none';
        });
      });
      return;
    }
  
    class CustomAccordion extends HTMLElement {
      constructor() {
        super();
        this._items = [];
        this._ro = null;            // ResizeObserver (if available)
        this._mo = null;            // MutationObserver fallback
        this._onKeydown = this._onKeydown.bind(this);
        this._onClick = this._onClick.bind(this);
        this._onResize = this._onResize.bind(this);
      }
  
      connectedCallback() {
        this._items = Array.from(this.querySelectorAll('.accordion-item'));
  
        // Prepare observers to keep open panels sized when content changes
        if ('ResizeObserver' in window) {
          this._ro = new ResizeObserver((entries) => {
            for (const entry of entries) {
              const content = entry.target;
              const item = content.closest('.accordion-item');
              if (item && item.classList.contains('open')) {
                // Keep maxHeight in sync with content height
                content.style.maxHeight = `${content.scrollHeight + 15}px`;
              }
            }
          });
        } else if ('MutationObserver' in window) {
          this._mo = new MutationObserver((mutations) => {
            mutations.forEach((m) => {
              const content = m.target.closest('.accordion-content');
              if (!content) return;
              const item = content.closest('.accordion-item');
              if (item && item.classList.contains('open')) {
                content.style.maxHeight = `${content.scrollHeight + 15}px`;
              }
            });
          });
        }
  
        // Setup each item
        this._items.forEach((item, idx) => {
          const header = item.querySelector('.accordion-header');
          const content = item.querySelector('.accordion-content');
          if (!header || !content) return;
  
          // Ensure the header is focusable & behaves like a button if it isn't one
          if (header.tagName.toLowerCase() !== 'button') {
            header.setAttribute('role', 'button');
            header.setAttribute('tabindex', '0');
          }
  
          // Link header ↔ content for screen readers
          const contentId = content.id || `${this._uniqueId('ProductAccordion')}-${idx}`;
          content.id = contentId;
          header.setAttribute('aria-controls', contentId);
          header.setAttribute('aria-expanded', item.classList.contains('open') ? 'true' : 'false');
  
          // Ensure content is hidden by default in CSS (max-height: 0; overflow: hidden;)
          if (!item.classList.contains('open')) {
            content.style.maxHeight = '0px';
          } else {
            // If marked open on page load, open immediately
            this._openAccordion(item, { animate: false });
          }
  
          // Events
          header.addEventListener('click', this._onClick);
          header.addEventListener('keydown', this._onKeydown);
  
          // Observe dynamic content changes
          if (this._ro) this._ro.observe(content);
          else if (this._mo) this._mo.observe(content, { childList: true, subtree: true });
          // Also update when images load inside content (classic gotcha)
          content.querySelectorAll('img').forEach((img) => {
            if (img.complete) return;
            img.addEventListener('load', () => {
              if (item.classList.contains('open')) {
                content.style.maxHeight = `${content.scrollHeight + 15}px`;
              }
            }, { once: true });
          });
  
          // Auto-open class support without setTimeout dependency
          if (item.classList.contains('open-on-page-load') && !item.classList.contains('open')) {
            this._openAccordion(item, { animate: false });
            item.classList.remove('open-on-page-load');
          }
        });
  
        // Resize fallback (e.g., fonts change, viewport resize)
        window.addEventListener('resize', this._onResize);
      }
  
      disconnectedCallback() {
        this._items.forEach((item) => {
          const header = item.querySelector('.accordion-header');
          header && header.removeEventListener('click', this._onClick);
          header && header.removeEventListener('keydown', this._onKeydown);
          const content = item.querySelector('.accordion-content');
          if (content && this._ro) this._ro.unobserve(content);
          if (this._mo) this._mo.disconnect();
        });
        window.removeEventListener('resize', this._onResize);
      }
  
      // Public method (kept for compatibility)
      openAccordion(item) {
        this._openAccordion(item, { animate: true });
      }
  
      // ---- Private helpers ----
      _onClick(e) {
        const header = e.currentTarget;
        const item = header.closest('.accordion-item');
        if (!item) return;
        if (item.classList.contains('open')) {
          this._closeAccordion(item);
        } else {
          this._openAccordion(item, { animate: true });
        }
      }
  
      _onKeydown(e) {
        // Space/Enter toggles; Up/Down to move focus between headers
        const key = e.key;
        const header = e.currentTarget;
        const headers = this._items
          .map((it) => it.querySelector('.accordion-header'))
          .filter(Boolean);
        const index = headers.indexOf(header);
  
        if (key === 'Enter' || key === ' ') {
          e.preventDefault();
          header.click();
        } else if (key === 'ArrowDown') {
          e.preventDefault();
          const next = headers[index + 1] || headers[0];
          next.focus();
        } else if (key === 'ArrowUp') {
          e.preventDefault();
          const prev = headers[index - 1] || headers[headers.length - 1];
          prev.focus();
        }
      }
  
      _onResize() {
        // Keep open panels sized correctly after viewport changes
        this._items.forEach((item) => {
          if (item.classList.contains('open')) {
            const content = item.querySelector('.accordion-content');
            if (content) {
              content.style.maxHeight = `${content.scrollHeight + 15}px`;
            }
          }
        });
      }
  
      _openAccordion(item, { animate } = { animate: true }) {
        const header = item.querySelector('.accordion-header');
        const content = item.querySelector('.accordion-content');
        if (!content) return;
  
        item.classList.add('open');
        header && header.setAttribute('aria-expanded', 'true');
  
        // Force a reflow when animating to allow transition from 0 -> height
        const target = `${content.scrollHeight + 15}px`;
        if (animate) {
          // If reduced motion is preferred, jump straight to target
          const prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          if (prefersReduced) {
            content.style.maxHeight = target;
          } else {
            // Ensure start state in case element had 'auto/none'
            content.style.transition = 'none';
            content.style.maxHeight = '0px';
            // next frame
            requestAnimationFrame(() => {
              content.style.transition = '';
              content.style.maxHeight = target;
            });
          }
        } else {
          content.style.maxHeight = target;
        }
      }
  
      _closeAccordion(item) {
        const header = item.querySelector('.accordion-header');
        const content = item.querySelector('.accordion-content');
        if (!content) return;
  
        item.classList.remove('open');
        header && header.setAttribute('aria-expanded', 'false');
  
        // Transition from current height down to 0
        const current = `${content.scrollHeight + 15}px`;
        content.style.maxHeight = current; // set the current height first
        // next frame for transition to apply
        requestAnimationFrame(() => {
          content.style.maxHeight = '0px';
        });
      }
  
      _uniqueId(prefix = 'id') {
        return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
      }
    }
  
    customElements.define('custom-accordion', CustomAccordion);
  })();
  